import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsapContext';
import MagneticButton from '../components/MagneticButton';
import Monolith from '../components/Monolith';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const yousifCredentials = [
  'Chartered Engineer (CEng)',
  'Member of the Institution of Engineering and Technology (MIET)',
  'AI automation — ~10 years of delivery',
  'United Kingdom',
];

const daniaCredentials = [
  'Biomedical Engineering',
  'AI automation & workflow systems',
  'Education & knowledge transfer',
];

const workTypes = [
  'Inbound and outbound AI agents with CRM integration',
  'Document processing pipelines for legal and accounting firms',
  'Workflow automation on Make.com, webhooks, and custom APIs',
  'AI-connected recruitment and qualification systems',
  'Internal operations tooling for professional services partnerships',
];

export default function About() {
  useDocumentMeta({
    title: 'About — Samia Systems',
    description:
      'The people behind Samia Systems. Engineering-grade AI automation for professional services firms.',
  });

  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scope = pageRef.current;
    if (!scope) return;
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        ['.about-label', '.about-h1'],
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power2.out', delay: 0.1 }
      );

      // Founder sections fade up
      ScrollTrigger.batch('.founder-section', {
        start: 'top 85%',
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, y: 40 },
            { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power2.out' }
          ),
      });

      // Founder content blocks
      ScrollTrigger.batch('.founder-content', {
        start: 'top 82%',
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, y: 24 },
            { opacity: 1, y: 0, duration: 0.65, stagger: 0.1, ease: 'power2.out' }
          ),
      });

      // Work type items stagger
      ScrollTrigger.batch('.work-type-item', {
        start: 'top 88%',
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, x: -16 },
            { opacity: 1, x: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out' }
          ),
      });

      // CTA section
      gsap.fromTo(
        '.about-cta-section',
        { opacity: 0, y: 32 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: { trigger: '.about-cta-section', start: 'top 85%', once: true },
        }
      );
    }, scope);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef}>
      <section className="grain relative bg-black px-6 pb-20 pt-40">
        <Monolith />
        <div className="container-x relative z-20">
          <p className="about-label label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            About
          </p>
          <h1 className="about-h1 max-w-4xl font-display text-hero font-light text-white text-balance">
            The people behind{' '}
            <span className="italic text-gold">Samia Systems.</span>
          </h1>
        </div>
      </section>

      <FounderSection
        name="Yousif Mohamed"
        accent="Engineer first."
        role="Founder & CEO · Samia Systems Limited"
        credentials={yousifCredentials}
        body={[
          'Samia Systems exists because the gap between an AI demo and a production system is wider than most consultancies will tell you. Prototypes fail in front of clients. Production systems fail quietly — until someone notices the cost, or the data loss, or the deal that never happened because the automation didn\'t fire.',
          'Yousif Mohamed founded Samia Systems Limited in the United Kingdom after a decade building AI-powered agents, workflow automation, and CRM-connected systems for businesses across legal, accounting, recruitment, and consulting. The work has covered inbound lead qualification, document intake, client onboarding, internal reporting, and AI agents that connect to live operational data via API — not playgrounds, production systems.',
          'Every engagement at Samia begins with a Technical Blueprint before a line of code is written. That Blueprint — architecture, data flow, credentials policy, test cases, edge cases, deployment plan — is the document that separates engineering from guessing. It is also the document that makes handover, auditing, and maintenance straightforward. The Blueprint is the contract; the code is its execution.',
        ]}
        quote="Done the thing. Paid for the mistakes. Documented every one."
      />

      <FounderSection
        name="Dania Mohamed"
        accent="Builds in public."
        role="Co-Founder · Samia Systems Limited"
        credentials={daniaCredentials}
        body={[
          'Biomedical engineering graduate who pivoted into AI systems and operational automation. Dania co-founded Samia Systems to build the kind of firm that documents everything — not just the deliverable, but the reasoning behind every architectural decision.',
          'At Samia Systems, Dania leads client education and knowledge transfer: translating engineering-grade methodology into structured frameworks that the firms adopting these systems can own, operate, and extend. The work is public. The learning curve is documented in full.',
          'The discipline is the same as Yousif\'s. The output is different: where Yousif writes the Blueprint, Dania writes the guide that makes the Blueprint legible to a partner at a firm that has never touched code.',
        ]}
        quote="Learning in public. Building in production."
      />

      <section className="grain border-t border-white/5 bg-black py-24">
        <div className="container-x">
          <p className="label mb-8 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Types of work
          </p>
          <ul className="grid gap-3 md:grid-cols-2">
            {workTypes.map((w) => (
              <li
                key={w}
                className="work-type-item flex items-start gap-3 font-sans text-body text-white/85"
              >
                <span className="mt-2 inline-block h-1 w-1 flex-shrink-0 rounded-full bg-gold" />
                {w}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="about-cta-section bg-black pb-32 pt-20">
        <div className="container-x flex flex-col items-center text-center">
          <h2 className="font-display text-section font-light text-white text-balance">
            Thirty minutes is enough to know if there's a fit.
          </h2>
          <p className="mt-6 max-w-xl font-sans text-body text-white/85">
            We listen first. If the engagement is not the right answer for your
            firm at this stage, we will say so.
          </p>
          <div className="mt-10">
            <MagneticButton to="/book">Book a Diagnostic</MagneticButton>
          </div>
        </div>
      </section>
    </div>
  );
}

function FounderSection({
  name,
  accent,
  role,
  credentials,
  body,
  quote,
}: {
  name: string;
  accent: string;
  role: string;
  credentials: string[];
  body: string[];
  quote: string;
}) {
  return (
    <section className="founder-section grain border-t border-white/5 bg-black py-24">
      <div className="container-x">
        <h2 className="max-w-3xl font-display text-section font-light text-white text-balance">
          {name}.{' '}
          <span className="italic text-gold">{accent}</span>
        </h2>

        <div className="founder-content mt-16 grid gap-16 md:grid-cols-[1fr_1.6fr]">
          <div>
            <p className="font-mono text-xs uppercase tracking-label text-gold">
              {role}
            </p>

            <ul className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6">
              {credentials.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 font-sans text-[0.78rem] uppercase tracking-label text-white/85"
                >
                  <span className="mt-1 inline-block h-px w-4 flex-shrink-0 bg-gold" />
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-6 font-sans text-body text-white/85">
            {body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <p className="font-display text-xl font-light italic text-white/95">
              "{quote}"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
